import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { withStyles } from '@material-ui/core/styles';

// import @material-ui/core
import AppBar from '@material-ui/core/AppBar';
import Paper from '@material-ui/core/Paper';
import Badge from '@material-ui/core/Badge';
import CircularProgress from '@material-ui/core/CircularProgress';
import Backdrop from '@material-ui/core/Backdrop';
// import Tabs
import { Tabs, Tab, TabPanel, a11yProps } from './../../fragments/Tabs';

// import styles-globalStyles
import globalStyles from './../../../styles/globalStyles';

import MaterialTable, {
  MTableToolbar,
  MTableHeader,
  MTableBody,
} from 'material-table';

// import constant
import { server, sortingOptions, muiTable } from './../../../constants';

// import routers constant
import { routers } from './../../../routers';

// import React-Redux & Reducer action
import { useSelector, useDispatch } from 'react-redux';
import * as issueActions from '../../../actions/issue.action';

// import utils
import {
  logger,
  formatDatetime,
  base64,
  getPeriodDateById,
} from './../../../utils/utils';
import { useHandleClientError } from './../../../utils/HttpClient';

// import fragmmet
import ColumnContent from './../../fragments/ColumnContent';
import AccordionForm from './../../fragments/AccordionForm';
import ColuumnSumUp from './../../fragments/ColuumnSumUp';

const StyledBadge = withStyles((theme) => ({
  badge: {
    right: -5,
    top: 2,
    border: `1px solid ${theme.palette.background.paper}`,
    padding: '2px',
  },
}))(Badge);

/**
 * styles
 * @param {*} theme
 */
const styles = (theme) => ({
  ...globalStyles(theme),
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    '& .MuiCardContent-root': {
      padding: theme.spacing(1),
    },
  },
  avatarSmall: {
    fontSize: '9px',
    fontWeight: 'normal',
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  appBar: {
    boxShadow: 'none',
    borderBottom: '1px solid #e8e8e8',
  },
  // https://stackoverflow.com/questions/55078256/how-to-change-the-position-of-an-icon-component-within-a-tab-component
  iconLabelWrapperReverse: {
    flexDirection: 'row-reverse',
  },
  toolbarWrapper: {
    '& .MuiToolbar-regular': {
      minHeight: 0,
    },
  },
});

/**
 * HelpdeskIssuesPage component
 * @param {*} props
 */
const HelpdeskIssuesPage = (props) => {
  // Classes extend from widthStyle(style)
  const { classes } = props;

  logger('HelpdeskIssuesPage render');

  const routFrontOf = routers.frontOf;

  // Hook handle-client-error
  const handleClientError = useHandleClientError();

  // TabPanel state & handle function
  const [tabValue, setTabValue] = React.useState(0);

  // TabPanel handle function
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  // Hook reducer V2) Destructure multiple reducer by one useSelector
  const issueListReducer = useSelector(
    ({ issueListReducer }) => issueListReducer
  );

  // Hook reducer (by useSelector) from store
  const dispatch = useDispatch();

  // Ref accordionForm
  const accordionFormRef = useRef(null);

  // Check is badgeContent assign data from getList (server-side)
  let isSeverBadgeContentRef = useRef(true);

  // issue-filters from catching (localStorage)
  const cacheIfs =
    useMemo(() => {
      let ifs = localStorage.getItem(server.ISSUE_FILTERS_KEY);
      return ifs ? base64.decodeObj(ifs) : {};
    }, []).reporter || {};
  const cacheFilters = useMemo(() => cacheIfs.filters || {}, []);
  const cacheSorts = useMemo(() => cacheIfs.sorts || {}, []);
  const cacheKeyword = useMemo(() => cacheIfs.keyword || '', []);

  useEffect(() => {
    // Must use cache period_id but dont use cache date
    let { dateStart, dateEnd } = getPeriodDateById(
      cacheFilters.period_id || '1y'
    );

    dispatch(
      issueActions.getList({
        package: base64.encodeObj({
          lang: 'TH',
          mode: server.REQ_PARAM_MODE_REPORTER,

          // Must use cache date from period_id but dont use cache date
          dateStart: dateStart,
          dateEnd: dateEnd,

          category_id: cacheFilters.category_id || undefined, // default category_id all (Cancel category_id prop by undefined value)
        }),
      })
    ).then(
      () => {
        setBadgeContent({
          open: mtOpenRef.current.state.data.length,
          close: mtCloseRef.current.state.data.length,
          all: mtAllRef.current.state.data.length,
        });
      },
      (error) => {
        throw error;
      }
    );
    return () => {
      logger('clearList');
      dispatch(issueActions.clearList());
    };
  }, []);

  const memoIssueListOpen = useMemo(
    () =>
      issueListReducer.result
        ? issueListReducer.result.filter(
            (issue) => issue.issueStatuses.id === 10
          )
        : [],
    [issueListReducer.result]
  );

  const memoIssueListClose = useMemo(
    () =>
      issueListReducer.result
        ? issueListReducer.result.filter(
            (issue) => issue.issueStatuses.id === 90
          )
        : [],
    [issueListReducer.result]
  );

  // Ref of material-table
  const mtOpenRef = useRef({});
  const mtCloseRef = useRef({});
  const mtAllRef = useRef({});

  // Ref of material-table toolbar (ref onSearchChange func)
  const mtToolbarOpenRef = useRef();
  const mtToolbarCloseRef = useRef();
  const mtToolbarAllRef = useRef();

  // Ref of material-table header (ref props.onOrderChange (sorting) func)
  const mtHeaderOpenRef = useRef();
  const mtHeaderCloseRef = useRef();
  const mtHeaderAllRef = useRef();

  // Ref of material-table body (ref props.onFilterChanged (fitering) func)
  const mtBodyOpenRef = useRef();
  const mtBodyCloseRef = useRef();
  const mtBodyAllRef = useRef();

  // Ref of material-table sorting (keep sorting value)
  const [sortingCol, setSortingCol] = useState({
    name: cacheSorts.name || sortingOptions.issues[0].name,
    title: cacheSorts.title || sortingOptions.issues[0].title,
    direction: cacheSorts.direction || 'desc',
  });

  // Ref of material-table filter (keep filtering value)
  const [filteringCol, setFilteringCol] = useState({
    name: 'issues.level',
    title: 'ระดับ',
    term: cacheFilters.permissionLevels || [1, 2, 3],
    customFilterAndSearch: (term, rowData) => {
      return term.includes(rowData.issues.level);
    },
  });

  const badgeMax = 9999;

  const [badgeContent, setBadgeContent] = useState({
    open: 0,
    close: 0,
    all: 0,
  });

  // useEffect 1st setBadgeContent
  useEffect(() => {
    if (issueListReducer.result && isSeverBadgeContentRef.current) {
      logger('(1) setBadgeContent depend on issueListReducer');
      setBadgeContent({
        open: memoIssueListOpen.length,
        close: memoIssueListClose.length,
        all: issueListReducer.result.length,
      });
      isSeverBadgeContentRef.current = false;
    }
  }, [issueListReducer.result]);

  // useEffect sortingCol
  useEffect(() => {
    mtHeaderOpenRef.current.props.onOrderChange(0, sortingCol.direction);
    mtHeaderCloseRef.current.props.onOrderChange(0, sortingCol.direction);
    mtHeaderAllRef.current.props.onOrderChange(0, sortingCol.direction);
    logger('useEffect onOrderChange');
  }, [sortingCol]);

  // useEffect filteringCol
  useEffect(() => {
    mtBodyOpenRef.current.props.onFilterChanged(1, filteringCol.term);
    mtBodyCloseRef.current.props.onFilterChanged(1, filteringCol.term);
    mtBodyAllRef.current.props.onFilterChanged(1, filteringCol.term);
    logger('useEffect onOrderChange');
  }, [filteringCol]);

  // Memo (func) handle search. (props onSearch of AccordionForm)
  const memoHandleSearchAndFilter = useCallback(
    async (
      {
        // SSP
        dateStart,
        dateEnd,
        category_id,
        permissionLevels,
      },
      keyword,
      { title, name, direction },
      side
    ) => {
      // Search (updating) (Do not check keyword that is existed or ever searched, course of material-table ref.searchText (form prev search) not clear)
      logger('search');
      mtToolbarOpenRef.current.onSearchChange(keyword);
      mtToolbarCloseRef.current.onSearchChange(keyword);
      mtToolbarAllRef.current.onSearchChange(keyword);

      // set sortingCol (updating)
      if (sortingCol.name !== name || sortingCol.direction !== direction) {
        logger('sort');
        setSortingCol({ title, name, direction });
      }

      // set fiteringCol (updating)
      if (filteringCol.term !== permissionLevels) {
        logger('filter');
        setFilteringCol((prev) => ({ ...prev, term: permissionLevels }));
      }

      if (side === 'SS') {
        isSeverBadgeContentRef.current = true;
        // Set isFilterAndSearch for prevent repleat useEffect 1st setBadgeContent
        // getList new data
        logger('getList');
        await dispatch(
          issueActions.getList({
            package: base64.encodeObj({
              lang: 'TH',
              mode: server.REQ_PARAM_MODE_REPORTER,
              dateStart,
              dateEnd,
              category_id,
            }),
          })
        ).catch((error) => handleClientError(error));
      }
    },
    [sortingCol, filteringCol]
  );

  // Memo (func) customFilterAndSearchSummary
  const memoCustomFilterAndSearchContent = useCallback((term, rowData) => {
    let searchSource = [
      rowData.issues.shortId,
      formatDatetime({ datetime: rowData.issues.createDate }),
      rowData.categorys.name,
      rowData.issues.summary,
      rowData.issues.fullname,
      rowData.processStatuses.name,
    ].join(' ');
    // Search and return result
    return searchSource.indexOf(term) != -1;
  }, []);

  const renderColumnContent = (rowData) => (
    <React.Fragment>
      <ColumnContent
        issueId={rowData.issues.shortId}
        createDate={formatDatetime({
          datetime: rowData.issues.createDate,
        })}
        categoryName={rowData.categorys.name}
        summary={rowData.issues.summary}
        path={`${routFrontOf.issue.pathNoParam}/${base64.encode(
          rowData.issues.id
        )}/${base64.encode(rowData.issues.shortId)}`}
        reporter={rowData.issues.fullname}
        worker={
          rowData.issueWorker &&
          `${rowData.issueWorker.firstName} ${rowData.issueWorker.lastName}`
        }
        level={rowData.issues.level}
        processStatus={{
          id: rowData.processStatuses.id,
          name: rowData.processStatuses.name,
          style: rowData.processStatuses.style,
        }}
        labels={
          rowData.issueLabels
            ? rowData.issueLabels.map((issueLabel) => issueLabel.labels)
            : []
        }
      />
    </React.Fragment>
  );

  const renderColumnSumUp = (rowData) => {
    // Fixed: (20240104) AJ. RAMA Submit incident but after that incident transaction not show.
    let latestCommentDate = rowData.IssueComments?.IssueCommentsLast
      ? formatDatetime({
          datetime: rowData.IssueComments.IssueCommentsLast.createDate,
        })
      : null;
    return (
      <ColuumnSumUp
        latestCommentDate={latestCommentDate}
        countComment={
          rowData.IssueComments?.count ? rowData.IssueComments.count : 0
        }
        isIssueStatusClosed={rowData.issueStatuses.id === 90}
      />
    );
  };

  return (
    <div className={classes.root}>
      <AccordionForm
        ref={accordionFormRef}
        sortsOptions={sortingOptions.issues}
        onFilterAndSearch={memoHandleSearchAndFilter}
      />
      <AppBar position="static" color="default" className={classes.appBar}>
        <Tabs
          value={tabValue}
          onChange={handleChangeTab}
          aria-label="simple tabs example"
        >
          <Tab
            classes={{
              wrapper: classes.iconLabelWrapperReverse,
            }}
            style={{ overflow: 'visible' }}
            label="เปิด"
            {...a11yProps(0)}
            icon={
              <StyledBadge
                badgeContent={badgeContent.open}
                max={badgeMax}
                color="secondary"
              >
                <div
                  style={{
                    width:
                      badgeContent.open < badgeMax
                        ? badgeContent.open.toString().length * 5
                        : 20,
                  }}
                ></div>
              </StyledBadge>
            }
          />
          <Tab
            classes={{
              wrapper: classes.iconLabelWrapperReverse,
            }}
            style={{ overflow: 'visible' }}
            label="ปิด"
            {...a11yProps(1)}
            icon={
              <StyledBadge
                badgeContent={badgeContent.close}
                max={badgeMax}
                color="secondary"
              >
                <div
                  style={{
                    width:
                      badgeContent.close < badgeMax
                        ? badgeContent.close.toString().length * 5
                        : 20,
                  }}
                ></div>
              </StyledBadge>
            }
          />

          <Tab
            classes={{
              wrapper: classes.iconLabelWrapperReverse,
            }}
            style={{ overflow: 'visible' }}
            label="ทั้งหมด"
            {...a11yProps(2)}
            icon={
              <StyledBadge
                badgeContent={badgeContent.all}
                max={badgeMax}
                color="secondary"
              >
                <div
                  style={{
                    width:
                      badgeContent.all < badgeMax
                        ? badgeContent.all.toString().length * 5
                        : 20,
                  }}
                ></div>
              </StyledBadge>
            }
          />
        </Tabs>
      </AppBar>
      <TabPanel value={tabValue} index={0}>
        <MaterialTable
          components={{
            Container: (props) => <Paper {...props} elevation={0} />,
            Toolbar: (props) => (
              <div className={classes.toolbarWrapper}>
                <MTableToolbar {...props} ref={mtToolbarOpenRef} />
              </div>
            ),
            Header: (props) => (
              <MTableHeader {...props} ref={mtHeaderOpenRef} />
            ),
            Body: (props) => <MTableBody {...props} ref={mtBodyOpenRef} />,
          }}
          tableRef={mtOpenRef}
          title=""
          /**
           * Bug:
           *  column as a variable couse of Material-Table (reder dat arow) freezes Browser
           * Issue:
           *  Material Table freezes Browser (https://github.com/mbrn/material-table/issues/2451)
           * Fixed:
           *  He puts column as a variable and put it outside the react component. His browser always hangs
           *  but if he puts it directly in the props column it works fine, He is using version v.1.69.0
           */
          columns={[
            //  Col 0: createDate (วันที่แจ้ง), for fiter (on server) and sort, hidden column
            {
              title: sortingCol.title,
              field: sortingCol.name,
              hidden: true,
              defaultSort: 'desc',
            },

            //  Col 1: issue level (ระดับ), for fiter (on client), hidden column
            {
              title: filteringCol.title,
              field: filteringCol.name,
              hidden: true,
              defaultFilter: filteringCol.term,
              customFilterAndSearch: filteringCol.customFilterAndSearch,
            },
            {
              title: 'รายการเหตุละเมิด',
              cellStyle: {
                width: '85%',
                maxWidth: '85%',
              },
              field: 'issues.summary',
              customFilterAndSearch: memoCustomFilterAndSearchContent,
              sorting: false,
              render: renderColumnContent,
            },
            {
              cellStyle: {
                width: 160,
              },
              sorting: false,
              render: renderColumnSumUp,
            },
          ]}
          data={memoIssueListOpen}
          onSearchChange={() => {
            if (!isSeverBadgeContentRef.current) {
              badgeContent.open != mtOpenRef.current.state.data.length &&
                setBadgeContent({
                  open: mtOpenRef.current.state.data.length,
                  close: mtCloseRef.current.state.data.length,
                  all: mtAllRef.current.state.data.length,
                });
            }
          }}
          options={{
            ...muiTable.options,
            search: false, // Hidden default materail-table search input
            searchText: cacheKeyword,
          }}
          localization={muiTable.localization}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <MaterialTable
          components={{
            Container: (props) => <Paper {...props} elevation={0} />,
            Toolbar: (props) => (
              <div className={classes.toolbarWrapper}>
                <MTableToolbar {...props} ref={mtToolbarCloseRef} />
              </div>
            ),
            Header: (props) => (
              <MTableHeader {...props} ref={mtHeaderCloseRef} />
            ),
            Body: (props) => <MTableBody {...props} ref={mtBodyCloseRef} />,
          }}
          tableRef={mtCloseRef}
          title=""
          /**
           * Bug:
           *  column as a variable couse of Material-Table freezes Browser
           * Issue:
           *  Material Table freezes Browser (https://github.com/mbrn/material-table/issues/2451)
           * Fixed:
           *  He puts column as a variable and put it outside the react component. His browser always hangs
           *  but if he puts it directly in the props column it works fine, He is using version v.1.69.0
           */
          columns={[
            //  Col 0: createDate (วันที่แจ้ง), for fiter (on server) and sort, hidden column
            {
              title: sortingCol.title,
              field: sortingCol.name,
              hidden: true,
              defaultSort: 'desc',
            },

            //  Col 1: issue level (ระดับ), for fiter (on client), hidden column
            {
              title: filteringCol.title,
              field: filteringCol.name,
              hidden: true,
              defaultFilter: filteringCol.term,
              customFilterAndSearch: filteringCol.customFilterAndSearch,
            },
            {
              title: 'รายการเหตุละเมิด',
              cellStyle: {
                width: '85%',
                maxWidth: '85%',
              },
              field: 'issues.summary',
              customFilterAndSearch: memoCustomFilterAndSearchContent,
              sorting: false,
              render: renderColumnContent,
            },
            {
              cellStyle: {
                width: 160,
              },
              sorting: false,
              render: renderColumnSumUp,
            },
          ]}
          data={memoIssueListClose}
          onSearchChange={() => {
            if (!isSeverBadgeContentRef.current) {
              badgeContent.close != mtCloseRef.current.state.data.length &&
                setBadgeContent({
                  open: mtOpenRef.current.state.data.length,
                  close: mtCloseRef.current.state.data.length,
                  all: mtAllRef.current.state.data.length,
                });
            }
          }}
          options={{
            ...muiTable.options,
            search: false, // Hidden default materail-table search input
            searchText: cacheKeyword,
          }}
          localization={muiTable.localization}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <MaterialTable
          components={{
            Container: (props) => <Paper {...props} elevation={0} />,
            Toolbar: (props) => (
              <div className={classes.toolbarWrapper}>
                <MTableToolbar {...props} ref={mtToolbarAllRef} />
              </div>
            ),
            Header: (props) => <MTableHeader {...props} ref={mtHeaderAllRef} />,
            Body: (props) => <MTableBody {...props} ref={mtBodyAllRef} />,
          }}
          tableRef={mtAllRef}
          title=""
          /**
           * Bug:
           *  column as a variable couse of Material-Table freezes Browser
           * Issue:
           *  Material Table freezes Browser (https://github.com/mbrn/material-table/issues/2451)
           * Fixed:
           *  He puts column as a variable and put it outside the react component. His browser always hangs
           *  but if he puts it directly in the props column it works fine, He is using version v.1.69.0
           */
          columns={[
            //  Col 0: createDate (วันที่แจ้ง), for filtering (on server) and sort, hidden column
            {
              title: sortingCol.title,
              field: sortingCol.name,
              hidden: true,
              defaultSort: 'desc',
            },

            //  Col 1: issue level (ระดับ), for filtering (on client), hidden column
            {
              title: filteringCol.title,
              field: filteringCol.name,
              hidden: true,
              defaultFilter: filteringCol.term,
              customFilterAndSearch: filteringCol.customFilterAndSearch,
            },
            {
              title: 'รายการเหตุละเมิด',
              cellStyle: {
                width: '85%',
                maxWidth: '85%',
              },
              field: 'issues.summary',
              customFilterAndSearch: memoCustomFilterAndSearchContent,
              sorting: false,
              render: renderColumnContent,
            },
            {
              cellStyle: {
                width: 160,
              },
              sorting: false,
              render: renderColumnSumUp,
            },
          ]}
          data={issueListReducer.result || []}
          onSearchChange={() => {
            if (!isSeverBadgeContentRef.current) {
              badgeContent.all != mtAllRef.current.state.data.length &&
                setBadgeContent({
                  open: mtOpenRef.current.state.data.length,
                  close: mtCloseRef.current.state.data.length,
                  all: mtAllRef.current.state.data.length,
                });
            }
          }}
          options={{
            ...muiTable.options,
            search: false, // Hidden default materail-table search input
            searchText: cacheKeyword,
          }}
          localization={muiTable.localization}
        />
      </TabPanel>
      <Backdrop
        className={classes.backdropStyle}
        open={issueListReducer.isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default withStyles(styles)(HelpdeskIssuesPage);
